import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useIsMedico } from "@/hooks/useIsMedico";
import { usePermissions } from "@/hooks/usePermissions";
import { apiFetch } from "@/lib/api";
import { Calendar, ChevronLeft, ChevronRight, FileText, Search, User } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiResponse, Doctor, ListarPacienteProps, PaginationInfo, Patient, TipoAtendimento } from "../interfaces/atendimento.interface";

const ListarPaciente = ({ isOpen, onClose, onSelectPatient }: ListarPacienteProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState("");
  const [filteredDoctors, setFilteredDoctors] = useState<Doctor[]>([]);
  const [tiposAtendimento, setTiposAtendimento] = useState<TipoAtendimento[]>([]);
  const [loadingTiposAtendimento, setLoadingTiposAtendimento] = useState(false);
  const [selectedTipoAtendimento, setSelectedTipoAtendimento] = useState<TipoAtendimento | null>(null);

  // Verificação de permissões e cargo
  const { isAdmin } = usePermissions();
  const { isMedico } = useIsMedico();

  // Função para buscar pacientes da API
  const fetchPatients = async (page: number = 1, search?: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "5",
        ...(search && { search })
      });

      const result: { result: ApiResponse } = await apiFetch(`/api/paciente?${params}`);

      if (!result.result || !result.result.data) {
        throw new Error('Estrutura de resposta inválida');
      }

      setPatients(result.result.data);
      setPagination(result.result.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('Erro ao buscar pacientes:', error);

      // Log mais detalhado do erro
      if (error instanceof Error) {
        console.error('Mensagem do erro:', error.message);
        console.error('Stack trace:', error.stack);
      }

      toast({
        title: "Erro de Conexão",
        description: `Não foi possível conectar com a API: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
        variant: "destructive"
      });

      // Limpar dados em caso de erro
      setPatients([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar médicos da API
  const fetchDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const result: { result: Doctor[] } = await apiFetch(`/api/medicos`);

      if (!result.result || !Array.isArray(result.result)) {
        throw new Error('Estrutura de resposta inválida');
      }

      setDoctors(result.result);
      setFilteredDoctors(result.result);
    } catch (error) {
      console.error('Erro ao buscar médicos:', error);

      toast({
        title: "Erro ao buscar médicos",
        description: `Não foi possível carregar os médicos: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
        variant: "destructive"
      });

      setDoctors([]);
      setFilteredDoctors([]);
    } finally {
      setLoadingDoctors(false);
    }
  };

  // Função para buscar tipos de atendimento da API
  const fetchTiposAtendimento = async () => {
    setLoadingTiposAtendimento(true);
    try {
      const result: { result: TipoAtendimento[] | ApiResponse } = await apiFetch(`/api/tipo_atendimento`);

      if (!result.result) {
        throw new Error('Estrutura de resposta inválida');
      }

      // Verificar se a resposta tem paginação ou é um array direto
      if (Array.isArray(result.result)) {
        setTiposAtendimento(result.result);
      } else {
        setTiposAtendimento([]);
      }
    } catch (error) {
      console.error('Erro ao buscar tipos de atendimento:', error);

      toast({
        title: "Erro ao buscar tipos de atendimento",
        description: `Não foi possível carregar os tipos de atendimento: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
        variant: "destructive"
      });

      setTiposAtendimento([]);
    } finally {
      setLoadingTiposAtendimento(false);
    }
  };

  // Carregar pacientes, médicos e tipos de atendimento quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      fetchPatients(1);
      fetchDoctors();
      fetchTiposAtendimento();
    }
  }, [isOpen]);

  // Debounce para busca de pacientes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== "") {
        fetchPatients(1, searchTerm);
      } else {
        fetchPatients(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Filtrar médicos conforme busca
  useEffect(() => {
    if (!doctorSearchTerm.trim()) {
      setFilteredDoctors(doctors);
    } else {
      const searchLower = doctorSearchTerm.toLowerCase();
      const filtered = doctors.filter(doctor => {
        const nome = doctor.pessoa?.nome?.toLowerCase() || '';
        const especialidade = doctor.especialidade?.nome?.toLowerCase() || '';
        const crm = doctor.crm?.toLowerCase() || '';
        return nome.includes(searchLower) || especialidade.includes(searchLower) || crm.includes(searchLower);
      });
      setFilteredDoctors(filtered);
    }
  }, [doctorSearchTerm, doctors]);

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    setSelectedDoctor(null); // Reset doctor selection when patient changes
    setSelectedTipoAtendimento(null); // Reset tipo atendimento when patient changes
    setSearchTerm(""); // Limpar busca de pacientes quando selecionar
  };

  const handleBackToPatientList = () => {
    setSelectedPatient(null);
    setSelectedDoctor(null);
    setSelectedTipoAtendimento(null);
    setDoctorSearchTerm("");
    fetchPatients(1); // Recarregar lista de pacientes
  };

  const handleConfirmSelection = () => {
    if (!selectedPatient || !selectedDoctor || !selectedTipoAtendimento) {
      toast({
        title: "Seleção incompleta",
        description: "Por favor, selecione um paciente, um médico e um tipo de atendimento.",
        variant: "destructive"
      });
      return;
    }

    // Transformar dados do paciente para o formato esperado
    const patientData = {
      id: selectedPatient.id,
      name: selectedPatient.pessoa.nome,
      cpf: selectedPatient.pessoa?.pessoaFisica?.cpf || '',
      birthDate: selectedPatient.pessoa.data_nascimento ?
        new Date(selectedPatient.pessoa.data_nascimento).toLocaleDateString('pt-BR') : '',
      phone: selectedPatient.pessoa.telefones?.[0]?.telefone || '',
      insurance: selectedPatient.convenios?.[0]?.nome || 'Particular'
    };

    onSelectPatient(patientData, selectedDoctor, selectedTipoAtendimento);
    onClose();

    // Reset form
    setSearchTerm("");
    setSelectedPatient(null);
    setSelectedDoctor(null);
    setSelectedTipoAtendimento(null);
    setDoctorSearchTerm("");
    setCurrentPage(1);
  };

  // Funções auxiliares para formatação
  const formatCPF = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone: string) => {
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Novo Atendimento - Selecionar Paciente e Médico
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Busca e Lista de Pacientes - Mostrar apenas se nenhum paciente selecionado */}
          {!selectedPatient && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="search">Buscar Paciente</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="Digite o nome ou CPF do paciente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Lista de Pacientes */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <p>Carregando pacientes...</p>
                  </div>
                ) : patients.length > 0 ? (
                  patients.map((patient) => (
                    <Card
                      key={patient.id}
                      className="p-4 cursor-pointer transition-colors hover:bg-accent/50"
                      onClick={() => handlePatientSelect(patient)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold">{patient.pessoa.nome}</h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span>CPF: {patient.pessoa?.pessoaFisica?.cpf ? formatCPF(patient.pessoa.pessoaFisica.cpf) : 'N/A'}</span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(patient.pessoa.data_nascimento)}
                            </span>
                            <span>{patient.pessoa.telefones?.[0]?.telefone ? formatPhone(patient.pessoa.telefones[0].telefone) : 'N/A'}</span>
                          </div>
                          {patient.convenios?.[0]?.nome && (
                            <Badge variant="secondary" className="mt-2">
                              {patient.convenios[0].nome}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum paciente encontrado</p>
                    <p className="text-sm">Tente ajustar os termos de busca</p>
                  </div>
                )}
              </div>

              {/* Controles de Paginação */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} pacientes
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchPatients(currentPage - 1, searchTerm || undefined)}
                      disabled={!pagination.hasPrev || loading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Anterior
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Página {pagination.page} de {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchPatients(currentPage + 1, searchTerm || undefined)}
                      disabled={!pagination.hasNext || loading}
                    >
                      Próxima
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Paciente Selecionado */}
          {selectedPatient && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Paciente Selecionado</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBackToPatientList}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Trocar Paciente
                </Button>
              </div>
              <Card className="p-4 border-primary bg-primary/5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{selectedPatient.pessoa.nome}</h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                      <span>CPF: {selectedPatient.pessoa?.pessoaFisica?.cpf ? formatCPF(selectedPatient.pessoa.pessoaFisica.cpf) : 'N/A'}</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(selectedPatient.pessoa.data_nascimento)}
                      </span>
                      <span>{selectedPatient.pessoa.telefones?.[0]?.telefone ? formatPhone(selectedPatient.pessoa.telefones[0].telefone) : 'N/A'}</span>
                    </div>
                    {selectedPatient.convenios?.[0]?.nome && (
                      <Badge variant="secondary" className="mt-2">
                        {selectedPatient.convenios[0].nome}
                      </Badge>
                    )}
                  </div>
                  <div className="text-primary">
                    <div className="w-8 h-8 rounded-full border-2 border-primary bg-primary/10 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Seleção de Médico */}
          {selectedPatient && (
            <div className="space-y-4 border-t pt-6">
              <div>
                <Label htmlFor="doctor">Médico Responsável</Label>
                <div className="space-y-2">
                  <Select
                    value={selectedDoctor?.id.toString()}
                    onValueChange={(value) => {
                      const doctor = filteredDoctors.find(d => d.id === parseInt(value));
                      setSelectedDoctor(doctor || null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o médico responsável..." />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingDoctors ? (
                        <div className="text-center py-4 text-muted-foreground">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                          <p className="text-sm">Carregando médicos...</p>
                        </div>
                      ) : filteredDoctors.length > 0 ? (
                        filteredDoctors.map((doctor) => (
                          <SelectItem key={doctor.id} value={doctor.id.toString()}>
                            <div className="flex flex-col">
                              <span className="font-medium">{doctor.pessoa?.nome || 'Nome não disponível'}</span>
                              <span className="text-sm text-muted-foreground">
                                {doctor.especialidade?.nome || 'Especialidade não disponível'}
                                {doctor.crm && doctor.uf_crm && ` • CRM: ${doctor.crm}/${doctor.uf_crm}`}
                              </span>
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <div className="text-center py-4 text-muted-foreground">
                          <p className="text-sm">Nenhum médico encontrado</p>
                          <p className="text-xs">Tente ajustar os termos de busca</p>
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedDoctor && (
                <Card className="p-4 bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{selectedDoctor.pessoa?.nome || 'Nome não disponível'}</h4>
                      <p className="text-sm text-muted-foreground">
                        {selectedDoctor.especialidade?.nome || 'Especialidade não disponível'}
                        {selectedDoctor.crm && selectedDoctor.uf_crm && ` • CRM: ${selectedDoctor.crm}/${selectedDoctor.uf_crm}`}
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Seleção de Tipo de Atendimento - Aparece após selecionar o médico */}
              {selectedDoctor && (
                <div className="space-y-2">
                  <Label htmlFor="tipoAtendimento">Tipo de Atendimento</Label>
                  <Select
                    value={selectedTipoAtendimento?.id.toString()}
                    onValueChange={(value) => {
                      const tipo = tiposAtendimento.find(t => t.id === parseInt(value));
                      setSelectedTipoAtendimento(tipo || null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo de atendimento..." />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingTiposAtendimento ? (
                        <div className="text-center py-4 text-muted-foreground">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                          <p className="text-sm">Carregando tipos de atendimento...</p>
                        </div>
                      ) : tiposAtendimento.length > 0 ? (
                        tiposAtendimento.map((tipo) => (
                          <SelectItem key={tipo.id} value={tipo.id.toString()}>
                            <div className="flex flex-col">
                              <span className="font-medium">{tipo.nome}</span>
                              {tipo.descricao && (
                                <span className="text-sm text-muted-foreground">{tipo.descricao}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <div className="text-center py-4 text-muted-foreground">
                          <p className="text-sm">Nenhum tipo de atendimento encontrado</p>
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedTipoAtendimento && (
                <Card className="p-4 bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{selectedTipoAtendimento.nome}</h4>
                      {selectedTipoAtendimento.descricao && (
                        <p className="text-sm text-muted-foreground">{selectedTipoAtendimento.descricao}</p>
                      )}
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Resumo da Seleção */}
          {selectedPatient && selectedDoctor && (
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h4 className="font-semibold mb-2">Resumo do Atendimento</h4>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Paciente:</span> {selectedPatient.pessoa.nome}</p>
                <p><span className="font-medium">CPF:</span> {selectedPatient.pessoa?.pessoaFisica?.cpf ? formatCPF(selectedPatient.pessoa.pessoaFisica.cpf) : 'N/A'}</p>
                <p><span className="font-medium">Médico:</span> {selectedDoctor.pessoa?.nome || 'Nome não disponível'}</p>
                <p><span className="font-medium">Especialidade:</span> {selectedDoctor.especialidade?.nome || 'Especialidade não disponível'}</p>
                {selectedDoctor.crm && selectedDoctor.uf_crm && (
                  <p><span className="font-medium">CRM:</span> {selectedDoctor.crm}/{selectedDoctor.uf_crm}</p>
                )}
                {selectedTipoAtendimento && (
                  <p><span className="font-medium">Tipo de Atendimento:</span> {selectedTipoAtendimento.nome}</p>
                )}
              </div>
            </Card>
          )}

          {/* Botões de Ação */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            {(isAdmin || isMedico) && (
              <Button
                onClick={handleConfirmSelection}
                disabled={!selectedPatient || !selectedDoctor || !selectedTipoAtendimento}
              >
                Iniciar Atendimento
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ListarPaciente;
